// components/ArrivalReadinessCard.js
//
// Admin dashboard card for the Phase 4 door-readiness assertion
// (tasks/lock-health-assertion-phase4.md). Answers ONE question for the day:
// "will today's arriving guests get through their doors, and do they
// have their instructions?"
//
// This is the PULL surface of the same readiness model the 06:50 morning email
// pushes — both render `doorReadiness/engine`'s structured result, so the card
// and the inbox can never disagree (the drift that killed the unanswered-
// messages card). It owns no business rules: every verdict arrives decided.
//
// ⚠️ ONE deliberate divergence since 2026-08-04: this card's verdict includes the
// CLEANING column; the 06:50 email's does not — it reads the door+message-only
// `lockOverall` and stays cleaning-blind by design (tasks/cleaning-readiness-signal.md
// §5.1, founder Q2 answer: email is being wound down in favour of push, so a third
// column was not added to it). A dirty room is therefore red here and absent from that
// email. That is a decision, not drift — do not "reconcile" them without reading §5.1.
//
// Design intent: the operator must not mentally aggregate five checks across
// several properties. The verdict banner states the day's answer in one line;
// everything below it is the evidence. Rows are ordered by severity, and a
// "ready" row stays deliberately quiet.
//
// Mirrors CheckinMonitorCard's card pattern (Bootstrap .card, loading/error
// states, brand colours, navigate-on-row-click, React Query hook) and its
// hardcoded Arabic/RTL + centralized dateFormatter convention.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useArrivalReadiness,
  useMarkCheckinMessageSentManually,
  useUndoCheckinMessageSentManually,
  useRecordGuestArrival,
  useUndoGuestArrival
} from '../hooks/useArrivalReadiness';
import { toRiyadhInputValue, riyadhInputValueToIso } from '../utils/dateValueHelper';
import useLanguage from '../hooks/useLanguage';
import { formatShortDateTime, formatRelativeTime } from '../utils/dateFormatter';
import ARRIVAL_ERROR_CODES, { ARRIVAL_MESSAGE_CODES, GUEST_ENTRY_STATUS } from '../utils/arrivalErrorCodes';
import BookingRefLine from './BookingRefLine';
import RelocationBanner from './RelocationBanner';
import useRelocations from '../hooks/useRelocations';
import { RELOCATION_UNKNOWN_AR } from '../config/relocationLabels';
import { cleaningReasonKey, CLEANING_REASON_CODES } from '../utils/cleaningReasonCodes';
// Shared with DepartureReadinessCard — the two cards stack on the dashboard and must stay visually
// identical siblings (see the stylesheet header). Class names here are unchanged.
import './readinessCards.css';

/**
 * True when the row will carry the manual-send attribution line, which states
 * "sent manually" in full. The message column's own detail says the same thing,
 * so the two must never render together.
 */
const isManualSendAttributed = (message) =>
  message?.code === ARRIVAL_MESSAGE_CODES.MANUAL_SEND_DONE
  && !!(message.sentManuallyBy || message.sentManuallyAt);

/**
 * Who ticked "sent manually", and when — on the row itself.
 *
 * The tick is a trust-based assertion with no delivery to verify against, so
 * accountability IS the feature. Until now that only existed in the booking's
 * change log, which nobody opens day to day. Name resolved server-side; the
 * timestamp goes through the centralized formatter like every other date here.
 */
const ManualSendAttribution = ({ message, bookingId }) => {
  const { t, language } = useLanguage();
  if (!isManualSendAttributed(message)) return null;

  // ONE line, not two. The column detail already said "sent manually" and this
  // line said it again with the name attached — the same sentence twice, stacked.
  // The attribution now carries the whole statement and the column detail is
  // suppressed at the call site, so who and when hang off the sentence that was
  // already there rather than repeating it.
  const who = message.sentManuallyBy
    ? ` ${t('arrivalReadiness.manualSend.by')} ${message.sentManuallyBy}`
    : '';
  const when = message.sentManuallyAt
    ? ` · ${formatShortDateTime(message.sentManuallyAt, language)}`
    : '';

  return (
    <small className="text-muted d-block">
      {t('arrivalReadiness.manualSend.done')}{who}{when}
      <UndoManualSend bookingId={bookingId} />
    </small>
  );
};

/**
 * Un-tick. Kept visually quiet — a plain link, not a button — because this is a
 * correction, not an action anyone should be invited to take. Without it a
 * mis-click leaves the arrival reading "handled" for the rest of the stay.
 */
const UndoManualSend = ({ bookingId }) => {
  const { t } = useLanguage();
  const { mutate, isPending } = useUndoCheckinMessageSentManually();

  return (
    <button
      type="button"
      className="btn btn-link btn-sm p-0"
      disabled={isPending}
      onClick={(e) => {
        e.stopPropagation();
        mutate(bookingId);
      }}
      style={{ fontSize: '12px', marginInlineStart: '8px', verticalAlign: 'baseline' }}
    >
      {t('arrivalReadiness.manualSend.undo')}
    </button>
  );
};

// The three possible answers. There is deliberately NO amber tier: an official
// amber immediately makes an operator ask "do I own this? do I act?" and
// dilutes the report. Still-syncing codes ride along as muted observations.
// Colours + icons only — every LABEL comes from the translation layer, matching
// AdminDashboardLockTable beside it. The reason/observation TEXT still arrives
// from the server already worded (the engines own it), so those stay as-is: the
// card never rewords a verdict it did not make.
const HEADLINE_STYLE = {
  ready: { color: '#0a8f3c', bg: '#eaf7ef', icon: 'bi-check-circle-fill' },
  action: { color: '#cc0000', bg: '#fdeded', icon: 'bi-exclamation-octagon-fill' },
  // An assertion must never quietly become an assumption: when the underlying
  // lock data is too stale to judge, we say so instead of showing green.
  unverified: { color: '#cc8800', bg: '#fff8e6', icon: 'bi-question-octagon-fill' }
};

const VERDICT_STYLE = {
  action: { key: 'action', color: '#cc0000' },
  unverified: { key: 'unverified', color: '#cc8800' },
  ready: { key: 'ready', color: '#0a8f3c' },
  na: { key: 'na', color: '#adb5bd' }
};

// The status badge, soft rather than a solid block. A column of solid red/green
// chips reads as a wall of alarm; a tinted pill with a saturated dot signals the
// same state without the noise (the "avoid a busy dashboard" ask). `accent` is
// the solid status colour, reused for the mobile card's edge stripe. Amber text
// is darkened from the chip's #cc8800 so it clears AA on its own tint.
const SOFT_BADGE = {
  action: { color: '#cc0000', bg: '#fdecec', accent: '#cc0000' },
  unverified: { color: '#9a6400', bg: '#fff4de', accent: '#cc8800' },
  ready: { color: '#0a8f3c', bg: '#e9f7ef', accent: '#0a8f3c' },
  na: { color: '#6c757d', bg: '#f1f1f4', accent: '#adb5bd' }
};

const badgeFor = (verdict) => SOFT_BADGE[(VERDICT_STYLE[verdict] || VERDICT_STYLE.unverified).key];

const StatusBadge = ({ verdict }) => {
  const { t } = useLanguage();
  const s = VERDICT_STYLE[verdict] || VERDICT_STYLE.unverified;
  const soft = SOFT_BADGE[s.key];
  return (
    <span className="arr-badge" style={{ color: soft.color, backgroundColor: soft.bg }}>
      {t(`arrivalReadiness.status.${s.key}`)}
    </span>
  );
};

// The guest is demonstrably inside — the lock recorded HIS code opening the door.
//
// Rendered ONLY on that positive evidence. There is deliberately no "not arrived"
// state: silence from the lock is equally "he hasn't come yet" and "he is outside
// right now on a code that failed", and a label implying the first would quietly
// stand down the operator in exactly the second case.
const GuestEntered = ({ entry }) => {
  const { t, language } = useLanguage();
  if (entry?.status !== GUEST_ENTRY_STATUS.ARRIVED) return null;
  return (
    <small className="d-block" style={{ color: '#0a8f3c', fontWeight: 500 }}>
      <i className="bi bi-door-open-fill me-1"></i>
      {t('arrivalReadiness.entry.arrived')}
      {entry.arrivedAt ? ` — ${formatShortDateTime(entry.arrivedAt, language)}` : ''}
    </small>
  );
};

/**
 * An OPS check-in confirmation (tasks/manual-guest-arrival-confirm.md) — a person's word that the
 * guest is inside, for a guest the lock never saw (let in on an internal/emergency code).
 *
 * 🔴 Never the lock's green, never «دخل الضيف»: a confirmation from a phone call must not read as
 * strong as the lock's evidence (the checkout record's D19 lesson). Grey, its own words, its own
 * icon. It also leaves the row's verdict alone — the guest's code is still broken.
 *
 * The hour shown is when the guest was inside (`observedAt`), not when the button was pressed.
 */
const OpsArrivalLine = ({ ops, bookingId }) => {
  const { t, language } = useLanguage();
  const { mutate, isPending } = useUndoGuestArrival();
  if (!ops) return null;
  const who = ops.recordedByName ? ` · ${t('arrivalReadiness.confirmArrival.by')} ${ops.recordedByName}` : '';
  return (
    <small className="text-muted d-block">
      <i className="bi bi-person-check" style={{ marginInlineEnd: '4px' }}></i>
      {t('arrivalReadiness.confirmArrival.confirmed')} — {formatShortDateTime(ops.observedAt, language)}{who}
      {/* A correction, not an action — a plain link, like the departures card's «تراجع». */}
      <button
        type="button"
        className="btn btn-link btn-sm p-0"
        disabled={isPending}
        onClick={(e) => { e.stopPropagation(); mutate(bookingId); }}
        style={{ fontSize: '12px', marginInlineStart: '8px', verticalAlign: 'baseline' }}
      >
        {t('arrivalReadiness.confirmArrival.undo')}
      </button>
    </small>
  );
};

/**
 * «تأكيد الدخول» — offered only where the backend says so (`canConfirmArrival`: arrival time passed,
 * no own-code entry, and the lock column shows a code problem). The card never re-derives that rule.
 *
 * QUIET by design: an underlined link, not a solid button — it is an option for the rare row where
 * the team let the guest in another way, not a job on every late guest. Two steps (link → time →
 * save), like «تأكيد الخروج»: one click would assert the guest got in at the moment someone pressed.
 */
const ArrivalConfirmAction = ({ bookingId }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const { mutate, isPending, isError, error } = useRecordGuestArrival();

  const stop = (e) => e.stopPropagation(); // the row navigates on click

  const openEntry = (e) => {
    stop(e);
    // Seeded on each open, never at mount: a card left sitting would prefill a stale "now".
    setValue(toRiyadhInputValue(new Date()));
    setOpen(true);
  };

  const save = (e) => {
    stop(e);
    // datetime-local carries no timezone — composed against Riyadh, or an operator abroad records
    // an hour nobody typed.
    const observedAt = riyadhInputValueToIso(value);
    if (!observedAt) return;
    mutate({ bookingId, observedAt }, { onSuccess: () => setOpen(false) });
  };

  // Translate the CODE, never the server's message text (I18N_STRATEGY).
  const code = error?.response?.data?.code;
  const errorKey = code === ARRIVAL_ERROR_CODES.CHECKIN_TIME_INVALID
    ? 'arrivalReadiness.confirmArrival.timeInvalid'
    : code === ARRIVAL_ERROR_CODES.CHECKIN_NOT_APPLICABLE
      ? 'arrivalReadiness.confirmArrival.notApplicable'
      : 'arrivalReadiness.confirmArrival.failed';

  if (!open) {
    return (
      <div style={{ marginBlockStart: '4px' }}>
        {/* touch-btn-sm: inside a row that navigates on click, a small target turns a mistap into
            "you are now on a different page". Underlined so it still reads as pressable. */}
        <button
          type="button"
          className="btn btn-sm touch-btn-sm btn-link p-0"
          onClick={openEntry}
          style={{ fontSize: '12px' }}
        >
          {t('arrivalReadiness.confirmArrival.action')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginBlockStart: '6px' }} onClick={stop}>
      {/* Input NESTED in its label: several rows can be open at once, so a fixed id would repeat. */}
      <label className="form-label mb-1 d-block" style={{ fontSize: '12px', fontWeight: 600 }}>
        {t('arrivalReadiness.confirmArrival.timeLabel')}
        <input
          type="datetime-local"
          className="form-control form-control-sm"
          value={value}
          // Not in the future — here for instant feedback AND server-side, since max is only a hint.
          max={toRiyadhInputValue(new Date())}
          onChange={(e) => setValue(e.target.value)}
          onClick={stop}
          style={{ maxWidth: '210px', fontSize: '13px', marginBlockStart: '4px' }}
        />
      </label>
      <small className="text-muted d-block" style={{ fontSize: '11px', marginBlockStart: '2px' }}>
        {t('arrivalReadiness.confirmArrival.timeHint')}
      </small>
      <div className="d-flex gap-2" style={{ marginBlockStart: '6px' }}>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary touch-btn-sm"
          disabled={isPending || !value}
          onClick={save}
          style={BRAND_OUTLINE_BTN}
        >
          {isPending
            ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            : <i className="bi bi-check2" style={{ marginInlineEnd: '4px' }} />}
          {t('arrivalReadiness.confirmArrival.save')}
        </button>
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          onClick={(e) => { stop(e); setOpen(false); }}
          style={{ fontSize: '12px' }}
        >
          {t('arrivalReadiness.confirmArrival.cancel')}
        </button>
      </div>
      {isError && (
        <small className="d-block" style={{ color: '#cc0000' }}>{t(errorKey)}</small>
      )}
    </div>
  );
};

// Bootstrap 5.3 buttons are driven by CSS variables, so setting these brands every state.
// Overriding plain `color`/`borderColor` instead leaves :hover filling with Bootstrap's default
// blue — brand purple text on a blue fill, both off-brand and low contrast. Shared by this card's
// two outline actions (manual send, check-in confirm save).
const BRAND_OUTLINE_BTN = {
  '--bs-btn-color': '#471396',
  '--bs-btn-border-color': '#471396',
  '--bs-btn-hover-bg': '#471396',
  '--bs-btn-hover-border-color': '#471396',
  '--bs-btn-hover-color': '#fff',
  '--bs-btn-active-bg': '#350f70',
  '--bs-btn-active-border-color': '#350f70',
  fontWeight: 600
};

// Per-column status. 'na' is styled as an explicit, muted entry with its own
// icon — visually distinct from a green tick, never a quiet pass.
const COLUMN_STYLE = {
  ok: { key: 'ready', color: '#0a8f3c', icon: 'bi-check-circle-fill' },
  ready: { key: 'ready', color: '#0a8f3c', icon: 'bi-check-circle-fill' },
  action: { key: 'action', color: '#cc0000', icon: 'bi-exclamation-octagon-fill' },
  unverified: { key: 'unverified', color: '#9a6400', icon: 'bi-question-circle-fill' },
  pending: { key: 'pending', color: '#6c757d', icon: 'bi-hourglass-split' },
  na: { key: 'na', color: '#adb5bd', icon: 'bi-dash-circle' }
};

// Severity order — the operator reads their risk first, their day second.
const VERDICT_RANK = { action: 0, unverified: 1, ready: 2, na: 3 };

const CHIPS = [
  { key: 'overallAction', label: 'action', color: '#cc0000' },
  { key: 'overallUnverified', label: 'unverified', color: '#cc8800' },
  { key: 'overallReady', label: 'ready', color: '#0a8f3c' },
  { key: 'noLock', label: 'noLock', color: '#6c757d' }
];

const CardShell = ({ children, language }) => (
  <div className="card ar-readiness" dir={language === 'ar' ? 'rtl' : 'ltr'}>
    <div className="card-body">{children}</div>
  </div>
);

const Header = () => {
  const { t } = useLanguage();
  return (
  <div className="mb-1">
    <h5 className="card-title mb-0" style={{ color: '#471396' }}>
      <i className="bi bi-door-open-fill me-2"></i>
      {t('arrivalReadiness.heading')}
    </h5>
    <small className="text-muted">{t('arrivalReadiness.subtitle')}</small>
  </div>
  );
};

/**
 * Collapse/expand. Open by default — a readiness card that hides itself on load defeats the point.
 *
 * The counters stay visible when collapsed: the operator folds a section away to get the OTHER one
 * on screen, so the folded card must still answer "is anything wrong here?" without reopening.
 * Only the verdict banner and the row table fold.
 *
 * Chevron is UP/DOWN, never left/right: Bootstrap Icons are glyphs and are not mirrored by the RTL
 * build, so a horizontal chevron would point the wrong way on this Arabic-first surface.
 */
const CollapseToggle = ({ expanded, onToggle, label }) => (
  <button
    type="button"
    className="btn btn-sm btn-link p-0 text-decoration-none"
    onClick={onToggle}
    aria-expanded={expanded}
    aria-label={label}
    title={label}
    style={{ color: '#471396', fontSize: '18px', lineHeight: 1 }}
  >
    <i className={`bi ${expanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
  </button>
);

/**
 * The cleaning column.
 *
 * Two things it does differently from its two siblings, both deliberate:
 *
 * 1. It translates off the backend's machine `reasonCode` instead of rendering the
 *    server's Arabic prose. The cleaning engine emits that code precisely so meaning is
 *    never read off display text (I18N_STRATEGY). The lock and message columns still
 *    render prose because they predate the contract and decoupling them needs backend
 *    changes; cleaning arrived with it, so it is wired up rather than re-buried. The
 *    server's own text stays as the fallback for a code this build has not learned yet.
 *
 * 2. It never falls back to 'na'. The engine has no not-applicable answer by design —
 *    every arrival needs a clean room, so "does not apply" is not a truth about
 *    cleaning. Without the explicit default, a missing `cleaning` object (an old cached
 *    bundle, a partial payload) would render the grey «لا ينطبق» chip, i.e. a clearing
 *    claim about something nothing checked — in the one card built to stop exactly that.
 */
const CleaningCell = ({ cleaning }) => {
  const { t, language } = useLanguage();
  const key = cleaningReasonKey(cleaning?.reasonCode);
  const translated = key ? t(key) : null;

  // WHEN, on the only two verdicts that have an answer. «اكتمل التنظيف» and «التنظيف جارٍ»
  // both told ops a state without its age — a clean finished ten minutes ago and one
  // finished yesterday read identically, and "in progress" said nothing about whether the
  // crew started five minutes or three hours ago.
  //
  // Keyed on the reason code, NOT the column status: 'unverified' also covers no-task,
  // unknown-arrival and assess-failed, none of which have a timestamp to show. Those
  // verdicts return before the engine builds its evidence, so the fields are absent
  // entirely — the truthiness guard below is doing real work, not defensive padding.
  const stamp = (() => {
    const code = cleaning?.reasonCode;
    if (code === CLEANING_REASON_CODES.DONE && cleaning?.completedAt) {
      return { label: t('arrivalReadiness.cleaningStamp.completed'), at: cleaning.completedAt };
    }
    if (code === CLEANING_REASON_CODES.IN_PROGRESS && cleaning?.startedAt) {
      return { label: t('arrivalReadiness.cleaningStamp.started'), at: cleaning.startedAt };
    }
    return null;
  })();

  return (
    <>
      <StatusCell
        status={cleaning?.status || 'unverified'}
        detail={translated || cleaning?.detail || null}
      />
      {/* Time alone when it happened today, date added once it is older. This card is a
          today-view, so the date is noise on the normal row — but a clean can legitimately
          finish the day before an arrival, and a bare «١٤:٣٠» there would read as today.
          Deliberately `formatRelativeTime` rather than the message column's
          `formatShortDateTime`, which always spells the date out. */}
      {stamp && (
        <small className="text-muted d-block" style={{ fontSize: '11px' }}>
          {stamp.label} {formatRelativeTime(stamp.at, language)}
        </small>
      )}
      {/* The ticket the operator has to go and act on. It was already in the payload
          and not shown, so a red cleaning column told them a room was dirty without
          telling them which task to open — the same gap BookingRefLine closed for the
          booking code. Shown whenever a task was matched, green included: on a green
          row it is the evidence for the claim, not a note about a problem. */}
      {cleaning?.ticketId && (
        <small className="text-muted d-block" style={{ fontSize: '11px' }}>
          {cleaning.ticketId}
        </small>
      )}
    </>
  );
};

// One readiness column (lock code / message). Reasons sit under the label in
// the column's own colour; observations stay muted — they inform, they are not
// tasks. The composer supplies both already worded; the card never rewords.
/**
 * The ops stop (tasks/unit-operational-status.md D17) — a fourth signal that is almost always clear,
 * so it gets no column of its own: it appears under the unit ONLY when the unit is stopped (or the
 * check failed), which is exactly when it turned the row red and the operator needs to know why.
 */
const UnitStopLine = ({ stop }) => {
  const { t } = useLanguage();
  if (!stop || stop.status === 'ready') return null;
  if (stop.status === 'unverified') {
    return <small className="text-muted d-block">{t('arrivalReadiness.unitStopUnverified')}</small>;
  }
  return (
    <small className="d-block arr-unit-stop">
      {t('arrivalReadiness.unitStopped')}
      {stop.stopReasonCode && ` (${t(`arrivalReadiness.stopReason.${stop.stopReasonCode}`)})`}
    </small>
  );
};

const StatusCell = ({ status, reasons = [], observations = [], detail = null }) => {
  // Resolves its own translations: passing `t` through every call site made a
  // forgotten prop a runtime crash (caught on dev, 2026-07-21).
  const { t } = useLanguage();
  const s = COLUMN_STYLE[status] || COLUMN_STYLE.na;
  // A GREEN column carries no notes (founder, dev review 2026-07-24). Observations
  // are context about the lock, not about this guest — "4 open alerts on the same
  // lock (other bookings)" was counting alerts on CANCELLED bookings, alerts with
  // no booking at all, and alerts for an arrival a week away. Printed beside a
  // green tick it reads as a warning about the guest in that row, which is the
  // opposite of what a green column is for. They still show on a column that is
  // red or couldn't-verify, where the operator is already looking for context.
  // Filtered by column status rather than by note text: observations carry no
  // machine code, and I18N_STRATEGY forbids inferring meaning from Arabic strings.
  const notes = s.key === 'ready' ? [] : observations.filter(Boolean);
  const blocking = reasons.filter(Boolean);
  return (
    <>
      <span style={{ color: s.color, fontWeight: 500, whiteSpace: 'nowrap' }}>
        <i className={`bi ${s.icon} me-1`}></i>{t(`arrivalReadiness.status.${s.key}`)}
      </span>
      {blocking.map((r, i) => (
        <small key={i} className="d-block" style={{ color: s.color }}>{r}</small>
      ))}
      {blocking.length === 0 && detail && (
        <small className="text-muted d-block">{detail}</small>
      )}
      {notes.map((n, i) => (
        <small key={`n${i}`} className="text-muted d-block">{n}</small>
      ))}
    </>
  );
};

/**
 * The one action an operator can take directly from this card (§7.7): a booking
 * with no OTA thread cannot be served by the automation, so someone has to send
 * the arrival instructions personally and say so here.
 *
 * Deliberately quiet — an outline button sized to the row, not a primary CTA. It
 * sits inside a clickable row, so it stops propagation; without that, ticking
 * would also navigate away to the booking and the operator would never see the
 * row clear.
 */
const ManualSendAction = ({ bookingId }) => {
  const { t } = useLanguage();
  const { mutate, isPending, isError, error } = useMarkCheckinMessageSentManually();

  // Translate the CODE, never the server's message text (I18N_STRATEGY). The one
  // refusal worth its own words is "this booking is automated after all" — it
  // means the row offering this button was stale, so telling the operator to
  // retry would be wrong advice.
  const code = error?.response?.data?.code;
  const errorKey = code === ARRIVAL_ERROR_CODES.MANUAL_SEND_NOT_APPLICABLE
    ? 'arrivalReadiness.manualSend.notApplicable'
    : 'arrivalReadiness.manualSend.failed';

  return (
    <div style={{ marginBlockStart: '4px' }}>
      {/* touch-btn-sm, not a bare btn-sm: this sits inside a row that navigates
          on click, so an undersized target turns a mistap into "you are now on
          a different page" — on the only action this card offers. */}
      <button
        type="button"
        className="btn btn-sm btn-outline-primary touch-btn-sm"
        disabled={isPending}
        onClick={(e) => {
          e.stopPropagation();
          mutate(bookingId);
        }}
        style={BRAND_OUTLINE_BTN}
      >
        {isPending ? (
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        ) : (
          <i className="bi bi-check2" style={{ marginInlineEnd: '4px' }} />
        )}
        {t('arrivalReadiness.manualSend.action')}
      </button>
      {isError && (
        <small className="d-block" style={{ color: '#cc0000' }}>
          {t(errorKey)}
        </small>
      )}
    </div>
  );
};

const ArrivalReadinessCard = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { report, isLoading, isError, refetch } = useArrivalReadiness();
  const [expanded, setExpanded] = useState(true);

  // Where each arriving guest ACTUALLY is. Every column on this card — the door code, the message,
  // the cleaning — is resolved from the BOOKING until stage 2 migrates them, so a relocated guest
  // reads here as being in a unit they are not in. This is the list an operator dispatches from.
  //
  // One batched call, not one per row: thirty requests for a fact that is null on twenty-nine of
  // them is how a list surface ends up quietly dropping the banner.
  //
  // No role gate: this card is mounted only on `/admin/dashboard`, which is `RoleGuard`-ed to admin,
  // and the endpoint behind it is core-staff-only anyway. If it is ever mounted somewhere looser,
  // that gate has to come with it.
  const arrivalRows = report?.arrivals || [];
  const { relocations, failed: relocationsFailed } = useRelocations(
    arrivalRows.map((a) => a.bookingId)
  );

  if (isLoading) {
    return (
      <CardShell language={language}>
        <Header />
        <div className="text-center p-4">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">{t('arrivalReadiness.loading')}</span>
          </div>
        </div>
      </CardShell>
    );
  }

  if (isError) {
    return (
      <CardShell language={language}>
        <Header />
        <div className="alert alert-danger mb-0 mt-2 d-flex justify-content-between align-items-center">
          <span>{t('arrivalReadiness.error')}</span>
          <button className="btn btn-sm btn-outline-danger" onClick={() => refetch()}>{t('arrivalReadiness.retry')}</button>
        </div>
      </CardShell>
    );
  }

  const { summary, arrivals, headline, lockStateAsOf } = report;
  const head = HEADLINE_STYLE[headline] || HEADLINE_STYLE.unverified;
  const headKey = HEADLINE_STYLE[headline] ? headline : 'unverified';
  const counts = { ...summary, noLock: summary.noLock };

  // The engine catches almost everything internally and returns a full map of
  // CLEANING_ASSESS_FAILED verdicts WITHOUT throwing, so `report.cleaningDegraded`
  // stays false on its two whole-set bailouts — the banner would never have appeared
  // in the outages it exists for. Derived from the rows instead, off the machine code:
  // if every arrival's cleaning answer is "could not assess", the column is down, and
  // an unexplained wall of amber is what makes operators stop reading a card.
  const cleaningRows = (arrivals || []).map((a) => a.cleaning).filter(Boolean);
  const cleaningUnavailable = report.cleaningDegraded
    || (cleaningRows.length > 0
        && cleaningRows.every((c) => c.reasonCode === CLEANING_REASON_CODES.ASSESS_FAILED));

  const rows = [...(arrivals || [])].sort(
    (a, b) => (VERDICT_RANK[a.overall] ?? 9) - (VERDICT_RANK[b.overall] ?? 9)
  );

  // Two day groups, and the split is load-bearing: today's guests are about the
  // DOOR (does the code open when they turn up); tomorrow's are about the
  // MESSAGE (their instructions go out 24h ahead, so that work is today's).
  // Showing only today would check the door for the right people and the
  // message for the wrong ones.
  const dayKeys = report.dayKeys || (report.dayKey ? [report.dayKey] : []);
  const groups = [
    { key: dayKeys[0], title: t('arrivalReadiness.day.today'), hint: t('arrivalReadiness.day.todayHint') },
    { key: dayKeys[1], title: t('arrivalReadiness.day.tomorrow'), hint: t('arrivalReadiness.day.tomorrowHint') }
  ].filter((g) => g.key);

  return (
    <CardShell language={language}>
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3 gap-3">
        <div className="d-flex align-items-center gap-2">
          <CollapseToggle
            expanded={expanded}
            onToggle={() => setExpanded((v) => !v)}
            label={t('arrivalReadiness.heading')}
          />
          <Header />
        </div>
        <div className="d-flex gap-3 flex-wrap">
          {CHIPS.map((c) => (
            <div key={c.key} className="text-center" style={{ minWidth: '64px' }}>
              <div style={{ fontWeight: 700, fontSize: '22px', color: counts[c.key] > 0 ? c.color : '#adb5bd' }}>
                {counts[c.key] ?? 0}
              </div>
              <small className="text-muted">{t(`arrivalReadiness.chip.${c.label}`)}</small>
            </div>
          ))}
        </div>
      </div>

      {/* We could not find out who has been moved. Said ONCE at the top rather than per row: the
          absence of a strip on a row is not distinguishable from "this guest is where the booking
          says", and on a dispatch list that difference is which apartment somebody walks to. */}
      {expanded && relocationsFailed && (
        <div className="alert alert-warning py-2 px-3 mb-3" dir="rtl" role="alert">
          <i className="bi bi-exclamation-triangle" aria-hidden="true"></i> {RELOCATION_UNKNOWN_AR}
        </div>
      )}

      {/* The day's answer in one line — so nobody has to aggregate five checks
          across several properties in their head.

          Suppressed when there are NO arrivals: "all of today's check-ins are ready — the door
          code is confirmed and valid at arrival time" is an assertion about guests who do not
          exist, printed directly above «لا يوجد تسجيل دخول اليوم أو غداً». Two contradictory
          sentences in one card, and the confident one is on top. A card that claims to have
          verified nothing must say nothing. */}
      {expanded && rows.length > 0 && (
        <div
          className="d-flex align-items-start gap-3 mb-3 p-3"
          style={{
            backgroundColor: head.bg,
            borderRight: `4px solid ${head.color}`,
            borderRadius: '6px'
          }}
        >
          <i className={`bi ${head.icon}`} style={{ color: head.color, fontSize: '20px', lineHeight: 1.4 }}></i>
          <div>
            <div style={{ color: head.color, fontWeight: 700, fontSize: '16px' }}>{t(`arrivalReadiness.headline.${headKey}`)}</div>
            <small className="text-muted">{t(`arrivalReadiness.headline.${headKey}Sub`)}</small>
          </div>
        </div>
      )}

      {/* Everything below folds away; the counters above stay, so a collapsed card still answers
          "is anything wrong here?" without being reopened. */}
      {expanded && (
        <>


      {/* Stated plainly rather than leaving every message column reading
          "could not verify" with no explanation. */}
      {report.messageDegraded && (
        <div className="alert alert-warning py-2 mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {t('arrivalReadiness.messageDegraded')}
        </div>
      )}

      {/* Same reasoning for the third column: an unexplained wall of "could not
          verify" is what makes operators stop reading a card. */}
      {cleaningUnavailable && (
        <div className="alert alert-warning py-2 mb-3">
          <i className="bi bi-exclamation-triangle" style={{ marginInlineEnd: '8px' }}></i>
          {t('arrivalReadiness.cleaningDegraded')}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-muted d-flex align-items-center gap-2">
          <i className="bi bi-calendar-x"></i>
          <span>{t('arrivalReadiness.empty')}</span>
        </div>
      ) : (
        groups.map((g) => {
          const dayRows = rows.filter((r) => r.dayKey === g.key);
          if (dayRows.length === 0) return null;
          return (
            <div key={g.key} className="mb-3">
              <div className="d-flex align-items-baseline flex-wrap gap-2 mb-1">
                <strong style={{ color: '#471396' }}>{g.title}</strong>
                <small className="text-muted">({dayRows.length}) — {g.hint}</small>
              </div>
              {/* Mobile: one stacked block per guest — seven RTL columns on a
                  phone would mean horizontal scrolling to reach the very
                  statuses the card exists to show. Same d-none d-md-block /
                  d-md-none split the ops performance dashboard uses. Cleaning is
                  a fourth stacked block here, deliberately not a redesign. */}
              <div className="d-md-none">
                {dayRows.map((a) => {
                  const soft = badgeFor(a.overall);
                  return (
                    <div
                      key={a.bookingId}
                      className="arr-m-row"
                      onClick={() => navigate(`/bookings/${a.bookingId}`)}
                      style={{
                        cursor: 'pointer',
                        opacity: a.overall === 'ready' ? 0.85 : 1,
                        // the status colour as an edge stripe, so a guest block
                        // reads its verdict before any label is read
                        borderInlineStartColor: soft.accent
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div style={{ minWidth: 0 }}>
                          <div className="fw-semibold" style={{ color: '#090040' }}>{a.guestName || '—'}</div>
                          <small className="text-muted d-block">{a.propertyLabel || '—'}</small>
                          {/* Directly under the unit it contradicts. Every other column on this row
                              is resolved from the booking, so this line is the only thing standing
                              between the operator and the wrong apartment. */}
                          <RelocationBanner relocation={relocations[a.bookingId]} compact />
                          <UnitStopLine stop={a.stop} />
                          {/* The code + channel were desktop-only; on a phone the
                              operator had no way to identify the booking they were
                              being asked to act on. */}
                          <BookingRefLine confirmationCode={a.confirmationCode} platform={a.platform} />
                        </div>
                        <div className="text-start flex-shrink-0">
                          <StatusBadge verdict={a.overall} />
                          <small className="text-muted d-block mt-1">
                            {a.arrivalAt ? formatShortDateTime(a.arrivalAt, language) : '—'}
                          </small>
                        </div>
                      </div>
                      {/* Its OWN full-width line, NOT inside the right-hand column above.
                          That column is flex-shrink-0 — it sizes to its widest child and
                          cannot give the space back — so a long sentence in it sets the
                          column width and starves the guest name beside it, which then
                          wraps one character per line. Seen on the PWA at 390px, invisible
                          in a wider responsive-mode viewport. */}
                      <GuestEntered entry={a.entry} />
                      <OpsArrivalLine ops={a.entry?.ops} bookingId={a.bookingId} />
                      {a.canConfirmArrival && <ArrivalConfirmAction bookingId={a.bookingId} />}
                      <div className="arr-m-sub">
                        <span className="arr-label">{t('arrivalReadiness.column.lockCode')}</span>
                        <div>
                          <StatusCell
                            status={a.door?.status}
                            reasons={a.door?.reasons}
                            observations={a.door?.observations}
                            detail={a.door?.detail}
                          />
                        </div>
                      </div>
                      <div className="arr-m-sub">
                        <span className="arr-label">{t('arrivalReadiness.column.message')}</span>
                        <div>
                          <StatusCell
                            status={a.message?.status}
                            detail={isManualSendAttributed(a.message) ? null : a.message?.detail}
                          />
                          <ManualSendAttribution message={a.message} bookingId={a.bookingId} />
                          {a.message?.code === ARRIVAL_MESSAGE_CODES.MANUAL_SEND_REQUIRED && (
                            <ManualSendAction bookingId={a.bookingId} />
                          )}
                        </div>
                      </div>
                      <div className="arr-m-sub">
                        <span className="arr-label">{t('arrivalReadiness.column.cleaning')}</span>
                        <div>
                          <CleaningCell cleaning={a.cleaning} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="table-responsive d-none d-md-block">
                <table className="table table-hover align-middle mb-0 arr-table">
                  <thead>
                    <tr>
                      <th>{t('arrivalReadiness.column.guest')}</th>
                      <th>{t('arrivalReadiness.column.property')}</th>
                      <th>{t('arrivalReadiness.column.time')}</th>
                      <th>{t('arrivalReadiness.column.lockCode')}</th>
                      <th>{t('arrivalReadiness.column.message')}</th>
                      <th>{t('arrivalReadiness.column.cleaning')}</th>
                      <th>{t('arrivalReadiness.column.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayRows.map((a) => {
                      return (
                        <tr
                          key={a.bookingId}
                          onClick={() => navigate(`/bookings/${a.bookingId}`)}
                          style={{ cursor: 'pointer', opacity: a.overall === 'ready' ? 0.75 : 1 }}
                        >
                          <td style={{ fontWeight: 500 }}>
                            {a.guestName || '—'}
                            <BookingRefLine confirmationCode={a.confirmationCode} platform={a.platform} />
                          </td>
                          <td>
                            {a.propertyLabel || '—'}
                            {/* In the property cell, not the guest cell: this column is the one
                                making the false claim for a relocated guest. */}
                            <RelocationBanner relocation={relocations[a.bookingId]} compact />
                            <UnitStopLine stop={a.stop} />
                          </td>
                          <td>
                            <small>{a.arrivalAt ? formatShortDateTime(a.arrivalAt, language) : '—'}</small>
                            <GuestEntered entry={a.entry} />
                            <OpsArrivalLine ops={a.entry?.ops} bookingId={a.bookingId} />
                            {a.canConfirmArrival && <ArrivalConfirmAction bookingId={a.bookingId} />}
                          </td>
                          <td>
                            <StatusCell
                              status={a.door?.status}
                              reasons={a.door?.reasons}
                              observations={a.door?.observations}
                              detail={a.door?.detail}
                            />
                          </td>
                          <td>
                            <StatusCell
                              status={a.message?.status}
                              detail={isManualSendAttributed(a.message) ? null : a.message?.detail}
                            />
                            <ManualSendAttribution message={a.message} bookingId={a.bookingId} />
                            {a.message?.code === ARRIVAL_MESSAGE_CODES.MANUAL_SEND_REQUIRED && (
                              <ManualSendAction bookingId={a.bookingId} />
                            )}
                          </td>
                          <td>
                            <CleaningCell cleaning={a.cleaning} />
                          </td>
                          <td>
                            <StatusBadge verdict={a.overall} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}

      {/* "Not applicable" is stated, never folded into the ready count — an
          arrival we cannot assert on must not look like one we cleared. */}
      {summary.noLock > 0 && (
        <small className="text-muted d-block mt-3">
          {summary.noLock} {t('arrivalReadiness.noLockSuffix')}
        </small>
      )}

      {/* Answers "how current is this?" without a live call to the lock vendor —
          the report's reliability must not depend on their uptime. */}
      {lockStateAsOf && (
        <small className="text-muted d-block mt-1">
          {t('arrivalReadiness.lockStateAsOf')} {formatShortDateTime(lockStateAsOf, language)} {t('arrivalReadiness.lockStateSource')}
        </small>
      )}
        </>
      )}
    </CardShell>
  );
};

export default ArrivalReadinessCard;
